/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button, Input, Label, theme } from "@nordcloud/gnui";
import { ChannelType, WebhookChannelType } from "~/generated/graphql";
import { FormGroup, Sidebar } from "~/components";
import { useDisclosure } from "~/hooks";
import { useUpdateNotificationGroup } from "~/views/notificationGroups/hooks/useUpdateNotificationGroup/useUpdateNotificationGroup";
import { useNotificationGroup } from "~/views/notificationGroups/notificationGroupDetails/NotificationGroupProvider";
import { slackSchema } from "~/views/notificationGroups/notificationGroupDetails/schema";

export function AddSlackForm() {
  const { data } = useNotificationGroup();
  const { isOpen, open, close } = useDisclosure();

  const onSubmit = (slackData: { webhookUrl: string }) => {
    updateNotificationGroup({
      id: data?.id,
      channels: [
        {
          type: ChannelType.Webhook,
          typeProperties: {
            webhook: {
              method: "POST",
              type: WebhookChannelType.Slack,
              url: slackData.webhookUrl,
            },
          },
        },
      ],
    });
  };

  const [updateNotificationGroup] = useUpdateNotificationGroup({
    onSuccess: () => {
      close();
      reset();
    },
  });

  const { handleSubmit, formState, register, reset } = useForm({
    resolver: zodResolver(slackSchema),
    defaultValues: {
      id: data?.id,
      webhookUrl: "",
    },
  });

  const error = formState.errors;

  return (
    <>
      <Button icon="plus" severity="low" onClick={open}>
        Add Slack Channel
      </Button>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Sidebar
          isOpen={isOpen}
          title="Add Slack Channel"
          onClick={close}
          footer={
            <div
              css={{
                margin: `${theme.spacing.spacing04} ${theme.spacing.spacing01}`,
              }}
            >
              <Button
                icon="checkmark"
                ml={theme.spacing.spacing04}
                type="submit"
              >
                Apply
              </Button>
            </div>
          }
        >
          <FormGroup error={error?.webhookUrl}>
            <Label name="Webhook URL" htmlFor="webookUrl" />
            <Input
              type="text"
              aria-label="provide webhook url"
              placeholder="e.g. https://hooks.slack.com/services/T12345678/B12345678/abcdefgh12345678"
              id="webookUrl"
              {...register("webhookUrl")}
            />
          </FormGroup>
        </Sidebar>
      </form>
    </>
  );
}
